<script lang="ts" setup>
/**
 * @name Dashboard.vue
 * @version 1.0.0
 * @description Vista para mostrar datos de bitácora de instituciones
 * @author
 * @changelog
 * 1.0.0 - 2024/01/01 - @Jorel254 - Versión inicial
 */

import { computed, onMounted, ref } from 'vue'
import BaseModal from '@/components/BaseModal.vue'
import { useStore } from 'vuex'
import { getInstitutionCounter } from '@/components/utils/functions'
import AsignaturasOrder from '../AsignaturasOrder.vue'
/**
 * @description Props del componente
 */

const error = ref<string | null>(null)
const successMessage = ref<string | null>(null)
const showErrorModal = ref(false)
const showSuccessModal = ref(false)

const store = useStore()
const currentInstitutionId = ref<number>(0)

onMounted(async () => {
	currentInstitutionId.value = await getInstitutionCounter()
})

const selectedInstitution = computed(() => store.state.global.selectedInstitution)

const updateShowSuccessModal = (value: { message: string; show: boolean }) => {
	successMessage.value = value.message
	showSuccessModal.value = value.show
}
const updateShowErrorModal = (value: { message: string; show: boolean }) => {
	error.value = value.message
	showErrorModal.value = value.show
}
const refreshPage = () => {
	try {
		showSuccessModal.value = false
		globalThis.location.reload()
	} catch (error) {
		console.error('Error al actualizar la página:', error)
	}
}
</script>

<template>
	<div class="asignaturas">
		<VaCard class="bitacora-card">
			<VaCardTitle class="header-gradient">
				<div class="header-content">
					<div class="header-main">
						<div class="header-icon-wrapper">
							<va-icon name="list_alt" class="header-icon" />
						</div>
						<div class="header-text">
							<h2 class="header-title">Orden de asignaturas</h2>
							<p class="header-subtitle">Organiza el orden de las asignaturas por carrera y guarda los cambios en la institución.</p>
						</div>
					</div>
				</div>
			</VaCardTitle>

			<VaCardContent class="card-content">
				<div class="pe-toolbar">
					<div class="pe-toolbar__meta">
						<span class="pe-chip">Institución #{{ currentInstitutionId || '—' }}</span>
						<span class="pe-chip pe-chip--accent">ID: {{ selectedInstitution?.idNombreInstitucion || 'Sin dato' }}</span>
						<p class="pe-hint">Arrastra y ordena las asignaturas de cada carrera según el plan de estudios institucional.</p>
					</div>
				</div>

				<section class="pe-section">
					<div class="pe-section__head">
						<va-icon name="sort" class="pe-section__icon" />
						<h3 class="pe-section__title">Asignaturas por carrera</h3>
					</div>
					<div class="pe-panel pe-panel--content">
						<AsignaturasOrder :carreas="selectedInstitution.Carrera" @update:showSuccessModal="updateShowSuccessModal" @update:showErrorModal="updateShowErrorModal" />
					</div>
				</section>
			</VaCardContent>
		</VaCard>

		<BaseModal v-model="showErrorModal" title="Error" :message="error || 'Error al guardar los datos'" :test-mode="true" size="medium">
			<template #body>
				<div class="error-message">
					<div class="error-header">
						<div class="error-icon">
							<i class="fas fa-exclamation-triangle"></i>
						</div>
						<h3 class="error-title">Error</h3>
					</div>
					<p class="error-description">{{ error || 'Error al guardar los datos' }}</p>
				</div>
			</template>
			<template #footer>
				<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
			</template>
		</BaseModal>
		<BaseModal v-model="showSuccessModal" title="" :message="successMessage || 'Datos guardados correctamente'" :test-mode="false" size="medium">
			<template #body>
				<div class="success-message">
					<div class="success-header">
						<div class="success-icon">
							<i class="fas fa-check-circle"></i>
						</div>
						<h3 class="success-title">Éxito</h3>
					</div>
					<p class="success-description">{{ successMessage || 'Datos actualizados correctamente' }}</p>
				</div>
			</template>
			<template #footer>
				<button class="btn btn-primary" @click="refreshPage()">Entendido</button>
			</template>
		</BaseModal>
	</div>
</template>

<style scoped>
.asignaturas {
	--pe-navy: #0f3569;
	--pe-navy-deep: #062342;
	--pe-accent: #24c8ff;
	--pe-accent-2: #169bff;
	--pe-border: rgba(15, 53, 105, 0.12);
	--pe-surface: #ffffff;
	--pe-canvas: #f4f7fb;
	--pe-text: #0f3569;
	--pe-text-2: rgba(15, 53, 105, 0.72);
	--pe-radius: 16px;
	--pe-radius-sm: 12px;
	--pe-shadow: 0 8px 24px rgba(15, 53, 105, 0.08);
	width: 100%;
	max-width: 100%;
	min-width: 0;
	overflow-x: hidden;
	box-sizing: border-box;
}

.bitacora-card {
	border-radius: var(--pe-radius);
	box-shadow: var(--pe-shadow);
	animation: fadeInUp 0.5s ease-out;
	overflow: hidden;
	background: var(--pe-surface);
	border: 1px solid var(--pe-border);
	width: 100%;
	max-width: 100%;
	min-width: 0;
}

.header-gradient {
	display: block;
	width: 100%;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	padding: 1.25rem 1.5rem;
	border-radius: 0;
	position: relative;
	text-transform: none;
	letter-spacing: normal;
	font-weight: 400;
}

.header-gradient::before {
	content: '';
	position: absolute;
	inset: 0;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	pointer-events: none;
}

.header-content {
	position: relative;
	z-index: 1;
	width: 100%;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 1.25rem;
	flex-wrap: wrap;
	width: 100%;
}

.header-icon-wrapper {
	width: 56px;
	height: 56px;
	border-radius: 14px;
	background: rgba(255, 255, 255, 0.14);
	backdrop-filter: blur(8px);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	border: 1px solid rgba(255, 255, 255, 0.18);
}

.header-icon {
	color: white;
	font-size: 1.75rem;
}

.header-text {
	flex: 1;
	min-width: 200px;
}

.header-title {
	font-size: 1.5rem;
	font-weight: 700;
	color: white;
	margin: 0;
	letter-spacing: -0.02em;
	text-transform: none;
	line-height: 1.2;
}

.header-subtitle {
	margin: 0.35rem 0 0;
	font-size: 0.85rem;
	color: rgba(255, 255, 255, 0.78);
	line-height: 1.45;
	max-width: 52ch;
	text-transform: none;
	letter-spacing: normal;
	font-weight: 400;
}

.card-content {
	padding: 1.5rem;
	background: var(--pe-canvas);
	overflow-x: hidden;
	overflow-y: auto;
	max-height: calc(100vh - 200px);
	min-width: 0;
}

.card-content::-webkit-scrollbar {
	width: 8px;
}

.card-content::-webkit-scrollbar-track {
	background: #e8eef5;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb {
	background: #9eb3cc;
	border-radius: 4px;
}

.pe-toolbar {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 1.25rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid var(--pe-border);
}

.pe-toolbar__meta {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem;
}

.pe-chip {
	display: inline-flex;
	align-items: center;
	padding: 0.3rem 0.75rem;
	border-radius: 999px;
	font-size: 0.75rem;
	font-weight: 600;
	color: var(--pe-text);
	background: rgba(15, 53, 105, 0.06);
	border: 1px solid var(--pe-border);
}

.pe-chip--accent {
	color: var(--pe-navy-deep);
	background: rgba(36, 200, 255, 0.12);
	border-color: rgba(36, 200, 255, 0.28);
}

.pe-hint {
	flex-basis: 100%;
	margin: 0.35rem 0 0;
	font-size: 0.875rem;
	color: var(--pe-text-2);
	line-height: 1.5;
}

.pe-section__head {
	display: flex;
	align-items: center;
	gap: 0.6rem;
	margin-bottom: 1rem;
}

.pe-section__icon {
	color: var(--pe-navy-deep);
	font-size: 1.25rem;
}

.pe-section__title {
	margin: 0;
	font-size: 1.05rem;
	font-weight: 700;
	color: var(--pe-text);
	letter-spacing: -0.01em;
}

.pe-section {
	min-width: 0;
}

.pe-panel {
	background: var(--pe-surface);
	border: 1px solid var(--pe-border);
	border-radius: var(--pe-radius-sm);
	padding: 1rem;
	min-width: 0;
	max-width: 100%;
}

.pe-panel--content {
	padding: 0.75rem;
	width: 100%;
	min-width: 0;
	overflow-x: auto;
}

.error-description,
.success-description {
	font-size: 1rem;
	line-height: 1.6;
	margin-bottom: 1rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
}

.error-description {
	border-left: 4px solid #f71d1d;
}

.success-description {
	border-left: 4px solid #28a745;
}

.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 1px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 24px rgba(254, 178, 178, 0.25);
	animation: fadeInUp 0.35s ease-out;
}

.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 1px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 24px rgba(40, 167, 69, 0.2);
	animation: fadeInUp 0.35s ease-out;
}

.error-header,
.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1rem;
	padding-bottom: 1rem;
	border-bottom: 1px solid rgba(255, 255, 255, 0.35);
}

.error-icon,
.success-icon {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.error-icon {
	background: #e67a7a;
	box-shadow: 0 4px 12px rgba(245, 101, 101, 0.35);
}

.success-icon {
	background: #1e7e34;
	box-shadow: 0 4px 12px rgba(40, 167, 69, 0.35);
}

.error-icon i,
.success-icon i {
	color: white;
	font-size: 1.35rem;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.35rem;
	font-weight: 700;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.35rem;
	font-weight: 700;
}

:deep(.va-tabs) {
	width: 100%;
}

:deep(.va-tabs__content) {
	width: 100%;
	display: block;
}

:deep(.va-tabs__content > .va-card) {
	width: 100% !important;
	max-width: 100% !important;
	margin-left: 0 !important;
	margin-right: 0 !important;
}

@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(16px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1rem;
	}

	.header-gradient {
		padding: 1rem;
	}

	.header-main {
		flex-direction: column;
		align-items: stretch;
	}
}
</style>
